import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import { ReactElement, useState } from 'react'
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium'
import { dataGridSx } from 'src/v3/manage/styles'
import { LightweightCourseV5 } from '../../../course/types'
import { getMigrationStatus } from '../../../course/utils/migration-status'
import MigrationStatusBadge from '../../../course/components/migration-status-badge'

let courseColumns: GridColDef<LightweightCourseV5>[] = [
  {
    field: 'name',
    headerName: 'Course',
    flex: 2,
    renderCell: ({ row }) => {
      let status = getMigrationStatus(row, row.classCount)
      let nonSelectable = status.kind !== 'none'
      return (
        <Box className="flex items-center gap-2">
          <span style={{ opacity: nonSelectable ? 0.4 : 1 }}>{row.name}</span>
          {nonSelectable && <MigrationStatusBadge status={status} />}
        </Box>
      )
    },
  },
  {
    field: 'length',
    headerName: 'CE Hours',
    width: 100,
    type: 'number',
    headerAlign: 'center',
    align: 'center',
  },
  {
    field: 'published',
    headerName: 'Status',
    width: 110,
    headerAlign: 'center',
    align: 'center',
    renderCell: ({ value }) => (
      <Chip
        label={value === 'published' ? 'Published' : 'Draft'}
        color={value === 'published' ? 'success' : 'warning'}
        size="small"
        className="w-full"
      />
    ),
  },
]

type AddCoursesModalProps = {
  isOpen: boolean
  seasonNumber: number
  availableCourses: LightweightCourseV5[]
  onClose: () => void
  onAdd: (courseIDs: string[]) => void
}

export default function AddCoursesModal({
  isOpen,
  seasonNumber,
  availableCourses,
  onClose,
  onAdd,
}: AddCoursesModalProps): ReactElement {
  let [pendingIDs, setPendingIDs] = useState<string[]>([])

  let handleClose = () => {
    setPendingIDs([])
    onClose()
  }

  let handleAdd = () => {
    onAdd(pendingIDs)
    setPendingIDs([])
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="add-courses-dialog-title"
    >
      <DialogTitle id="add-courses-dialog-title">
        Add Courses to Season {seasonNumber}
      </DialogTitle>
      <DialogContent>
        {availableCourses.length === 0 ? (
          <Box className="py-8 text-center">
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              All courses for this linked service have already been added.
            </Typography>
          </Box>
        ) : (
          <DataGridPremium
            rows={availableCourses}
            columns={courseColumns}
            checkboxSelection
            disableRowSelectionOnClick
            isRowSelectable={({ row }) =>
              getMigrationStatus(row, row.classCount).kind === 'none'
            }
            rowSelectionModel={{
              type: 'include',
              ids: new Set(pendingIDs),
            }}
            onRowSelectionModelChange={(model) => {
              setPendingIDs(
                Array.from(
                  model.type === 'include' ? model.ids : new Set<string>(),
                ) as string[],
              )
            }}
            autoHeight
            sx={dataGridSx}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={pendingIDs.length === 0}
        >
          Add {pendingIDs.length > 0 ? `${pendingIDs.length} ` : ''}
          {pendingIDs.length === 1 ? 'Course' : 'Courses'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
